import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Modal } from "antd";
import "./replayVideoDownload.css";

// «Descargar partido»: crea un MP4 de la repetición a partir de los datos de la
// partida (no graba la pantalla). Usuarios: en su computadora. Administradores:
// eligen su computadora o el servidor.
const SCRIPT_SOURCES = ["/replay-video/mp4-muxer.js?v=5", "/replay-video/exporter.js?v=5"];
const loadedScripts = new Map();

const loadScript = (src) => {
  if (loadedScripts.has(src)) return loadedScripts.get(src);
  const promise = new Promise((resolve, reject) => {
    const element = document.createElement("script");
    element.src = src;
    element.async = true;
    element.onload = () => resolve();
    element.onerror = () => {
      loadedScripts.delete(src);
      reject(new Error("No se pudo cargar el creador de videos."));
    };
    document.head.appendChild(element);
  });
  loadedScripts.set(src, promise);
  return promise;
};

const FORMAT_OPTIONS = [
  { id: "landscape", label: "Horizontal", ratio: "16:9", size: "1920 × 1080", hint: "YouTube, ordenador, TV" },
  { id: "portrait", label: "Vertical", ratio: "9:16", size: "1080 × 1920", hint: "TikTok, Reels, Shorts" },
];

const PHASE_LABELS = {
  review: "El bot está revisando cada jugada…",
  loading: "Cargando el tablero…",
  recording: "Grabando las jugadas…",
  audio: "Mezclando el sonido…",
  encoding: "Preparando el archivo…",
  queued: "Esperando turno en el servidor…",
  rendering: "Creando el video en el servidor…",
  downloading: "Descargando el video…",
  done: "¡Listo!",
};

const slugify = (value) =>
  String(value || "partida")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .toLowerCase() || "partida";

const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const readErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch (_error) {
      // Respuesta sin JSON.
    }
  }
  return data?.message || error?.message || fallback;
};

const ReplayVideoDownload = ({ matchId, matchName, isAdmin, unityUrl, canReview = true, className = "" }) => {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [format, setFormat] = useState("landscape");
  const [where, setWhere] = useState("local");
  const [withReview, setWithReview] = useState(false);
  const [task, setTask] = useState(null);
  const abortRef = useRef(null);
  const serverJobRef = useRef("");
  const pollTimerRef = useRef(null);
  const busy = Boolean(task && !task.finished);

  const stopPolling = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  useEffect(
    () => () => {
      stopPolling();
      if (abortRef.current) abortRef.current.abort();
      if (serverJobRef.current) {
        axios.delete(`/replays/video-jobs/${serverJobRef.current}`).catch(() => {});
      }
    },
    []
  );

  const buildFileName = useCallback(
    (selectedFormat) =>
      `xima-${slugify(matchName)}-${selectedFormat === "portrait" ? "vertical" : "horizontal"}.mp4`,
    [matchName]
  );

  const waitForReview = useCallback(
    async (signal) => {
      for (;;) {
        if (signal?.aborted) {
          const abort = new Error("Cancelado.");
          abort.name = "AbortError";
          throw abort;
        }
        const response = await axios.get(`/replays/${matchId}/analysis`);
        const analysis = response?.data?.analysis;
        if (analysis?.state === "done") return Array.isArray(analysis?.result?.moves) ? analysis.result.moves : [];
        if (analysis?.state === "error") throw new Error(analysis.error || "No se pudo revisar la partida.");
        setTask((prev) =>
          prev && !prev.finished
            ? { ...prev, phase: "review", percent: Math.round(Number(analysis?.progress) || 0) }
            : prev
        );
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    },
    [matchId]
  );

  const runLocal = useCallback(
    async (selectedFormat, includeReview) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setTask({ where: "local", phase: includeReview ? "review" : "loading", percent: 0 });
      try {
        const review = includeReview ? await waitForReview(controller.signal) : null;
        setTask((prev) => (prev && !prev.finished ? { ...prev, phase: "loading", percent: 0 } : prev));
        await Promise.all(SCRIPT_SOURCES.map(loadScript));
        if (!window.XimaReplayVideo || !window.Mp4Muxer) {
          SCRIPT_SOURCES.forEach((src) => loadedScripts.delete(src));
          throw new Error("No se pudo cargar el creador de videos. Recarga la página e inténtalo otra vez.");
        }
        const response = await axios.get(`/replays/${matchId}/video-script`);
        const script = response?.data?.script;
        if (!script?.steps?.length) throw new Error("Esta partida no tiene jugadas para el video.");
        if (review) script.review = review;
        const result = await window.XimaReplayVideo.exportReplay({
          unityUrl,
          script,
          format: selectedFormat,
          signal: controller.signal,
          onProgress: (progress) =>
            setTask((prev) =>
              prev && !prev.finished
                ? { ...prev, phase: progress.phase, percent: Math.round(progress.percent) }
                : prev
            ),
        });
        saveBlob(result.blob, buildFileName(selectedFormat));
        setTask({
          where: "local",
          phase: "done",
          percent: 100,
          finished: true,
          notes: result.notes || [],
          sizeMb: result.blob.size / (1024 * 1024),
        });
      } catch (error) {
        if (error?.name === "AbortError") {
          setTask(null);
          return;
        }
        setTask({
          where: "local",
          finished: true,
          error: await readErrorMessage(error, "No se pudo crear el video."),
        });
      } finally {
        abortRef.current = null;
      }
    },
    [buildFileName, matchId, unityUrl, waitForReview]
  );

  const downloadServerFile = useCallback(
    async (jobId, selectedFormat) => {
      setTask((prev) => ({ ...(prev || {}), where: "server", phase: "downloading", percent: 0 }));
      const response = await axios.get(`/replays/video-jobs/${jobId}/file`, {
        responseType: "blob",
        onDownloadProgress: (event) => {
          if (!event.total) return;
          setTask((prev) =>
            prev && !prev.finished
              ? { ...prev, phase: "downloading", percent: Math.round((event.loaded / event.total) * 100) }
              : prev
          );
        },
      });
      serverJobRef.current = "";
      saveBlob(response.data, buildFileName(selectedFormat));
      setTask({
        where: "server",
        phase: "done",
        percent: 100,
        finished: true,
        notes: [],
        sizeMb: response.data.size / (1024 * 1024),
      });
    },
    [buildFileName]
  );

  const runServer = useCallback(
    async (selectedFormat, includeReview) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setTask({ where: "server", phase: includeReview ? "review" : "queued", percent: 0 });
      try {
        if (includeReview) await waitForReview(controller.signal);
        abortRef.current = null;
        setTask((prev) => (prev && !prev.finished ? { ...prev, phase: "queued", percent: 0 } : prev));
        const response = await axios.post(`/replays/${matchId}/video-render`, {
          format: selectedFormat,
          withReview: Boolean(includeReview),
        });
        const jobId = response?.data?.job?.id;
        if (!jobId) throw new Error("El servidor no empezó el video.");
        serverJobRef.current = jobId;
        const startedAt = Date.now();
        const poll = async () => {
          try {
            const status = await axios.get(`/replays/video-jobs/${jobId}`);
            const job = status?.data?.job;
            if (!serverJobRef.current) return;
            if (job?.state === "done") {
              await downloadServerFile(jobId, selectedFormat);
              return;
            }
            if (job?.state === "error") {
              serverJobRef.current = "";
              setTask({ where: "server", finished: true, error: job.error || "El servidor no pudo crear el video." });
              return;
            }
            setTask((prev) =>
              prev && !prev.finished
                ? {
                    ...prev,
                    phase: job?.phase === "recording" || job?.phase === "loading" ? "rendering" : job?.phase || "rendering",
                    serverPhase: job?.phase,
                    percent: Math.round(Number(job?.progress) || 0),
                    elapsedSec: Math.round((Date.now() - startedAt) / 1000),
                  }
                : prev
            );
            pollTimerRef.current = setTimeout(poll, 2000);
          } catch (error) {
            serverJobRef.current = "";
            setTask({
              where: "server",
              finished: true,
              error: await readErrorMessage(error, "Se perdió el contacto con el video del servidor."),
            });
          }
        };
        pollTimerRef.current = setTimeout(poll, 1500);
      } catch (error) {
        serverJobRef.current = "";
        if (error?.name === "AbortError") {
          setTask(null);
          return;
        }
        setTask({
          where: "server",
          finished: true,
          error: await readErrorMessage(error, "No se pudo empezar el video en el servidor."),
        });
      }
    },
    [downloadServerFile, matchId, waitForReview]
  );

  const handleStart = () => {
    setChooserOpen(false);
    const includeReview = canReview && withReview;
    if (isAdmin && where === "server") runServer(format, includeReview);
    else runLocal(format, includeReview);
  };

  const handleCancel = () => {
    stopPolling();
    if (abortRef.current) abortRef.current.abort();
    if (serverJobRef.current) {
      axios.delete(`/replays/video-jobs/${serverJobRef.current}`).catch(() => {});
      serverJobRef.current = "";
    }
    setTask(null);
  };

  const percent = Math.max(0, Math.min(100, Number(task?.percent) || 0));

  return (
    <>
      <button
        type="button"
        className={`rv-download-btn ${className}`}
        onClick={() => setChooserOpen(true)}
        disabled={!matchId || busy}
      >
        <span aria-hidden="true">⬇</span>
        {busy ? `Creando video… ${percent} %` : "Descargar partido"}
      </button>

      <Modal
        open={chooserOpen}
        onCancel={() => setChooserOpen(false)}
        footer={null}
        centered
        width={560}
        zIndex={1300}
        rootClassName="rv-modal"
        title="Descargar partido en video (MP4)"
      >
        <p className="rv-lead">
          El video se crea a partir de las jugadas guardadas, con la cámara siguiendo cada jugada y los
          sonidos del juego.
        </p>
        <div className="rv-label">Formato</div>
        <div className="rv-options">
          {FORMAT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`rv-option${format === option.id ? " is-on" : ""}`}
              onClick={() => setFormat(option.id)}
            >
              <span className={`rv-shape rv-shape-${option.id}`} aria-hidden="true" />
              <strong>
                {option.label} <em>{option.ratio}</em>
              </strong>
              <small>{option.size}</small>
              <small>{option.hint}</small>
            </button>
          ))}
        </div>
        {canReview ? (
          <>
            <div className="rv-label">Tipo de video</div>
            <div className="rv-options">
              <button
                type="button"
                className={`rv-option${withReview ? " is-on" : ""}`}
                onClick={() => setWithReview(true)}
              >
                <span className="rv-icon" aria-hidden="true">★</span>
                <strong>Con revisión del partido</strong>
                <small>Cada bloque lleva su nota (brillante, excelente, error…) y la flecha verde del bot.</small>
              </button>
              <button
                type="button"
                className={`rv-option${!withReview ? " is-on" : ""}`}
                onClick={() => setWithReview(false)}
              >
                <span className="rv-icon" aria-hidden="true">▶</span>
                <strong>Normal, sin revisión</strong>
                <small>La partida tal como se jugó.</small>
              </button>
            </div>
          </>
        ) : null}
        {isAdmin ? (
          <>
            <div className="rv-label">¿Dónde se convierte?</div>
            <div className="rv-options">
              <button
                type="button"
                className={`rv-option${where === "local" ? " is-on" : ""}`}
                onClick={() => setWhere("local")}
              >
                <span className="rv-icon" aria-hidden="true">💻</span>
                <strong>Convertir en mi computadora</strong>
                <small>Usa los recursos de este equipo. Deja la pestaña abierta.</small>
              </button>
              <button
                type="button"
                className={`rv-option${where === "server" ? " is-on" : ""}`}
                onClick={() => setWhere("server")}
              >
                <span className="rv-icon" aria-hidden="true">🖥️</span>
                <strong>Convertir en el servidor</strong>
                <small>Usa los recursos del servidor. El archivo se borra al descargarlo.</small>
              </button>
            </div>
          </>
        ) : null}
        <div className="rv-actions">
          <button type="button" className="rv-secondary" onClick={() => setChooserOpen(false)}>
            Cancelar
          </button>
          <button type="button" className="rv-primary" onClick={handleStart}>
            Crear video
          </button>
        </div>
      </Modal>

      <Modal
        open={Boolean(task)}
        onCancel={task?.finished ? () => setTask(null) : undefined}
        closable={Boolean(task?.finished)}
        maskClosable={false}
        footer={null}
        centered
        width={480}
        zIndex={1300}
        rootClassName="rv-modal"
        title={task?.error ? "No se pudo crear el video" : task?.finished ? "Video listo" : "Creando video…"}
      >
        {task?.error ? (
          <>
            <p className="rv-error">{task.error}</p>
            <div className="rv-actions">
              <button type="button" className="rv-primary" onClick={() => setTask(null)}>
                Cerrar
              </button>
            </div>
          </>
        ) : task?.finished ? (
          <>
            <p className="rv-lead">
              El video se ha descargado
              {task.sizeMb ? ` (${task.sizeMb.toFixed(1)} MB)` : ""}. Búscalo en tu carpeta de descargas.
            </p>
            {(task.notes || []).map((note) => (
              <p key={note} className="rv-note">
                {note}
              </p>
            ))}
            <div className="rv-actions">
              <button type="button" className="rv-primary" onClick={() => setTask(null)}>
                Cerrar
              </button>
            </div>
          </>
        ) : task ? (
          <>
            <div className="rv-progress-head">
              <span>{PHASE_LABELS[task.phase] || "Creando video…"}</span>
              <strong>{percent} %</strong>
            </div>
            <div className="rv-progress">
              <div style={{ width: `${percent}%` }} />
            </div>
            <p className="rv-note">
              {task.where === "server"
                ? `Se está creando en el servidor${task.elapsedSec ? ` · ${Math.floor(task.elapsedSec / 60)} min ${task.elapsedSec % 60} s` : ""}. Puedes seguir viendo la repetición.`
                : "Se está creando en tu computadora. Deja esta pestaña abierta; puedes seguir viendo la repetición."}
            </p>
            <div className="rv-actions">
              <button type="button" className="rv-secondary" onClick={handleCancel}>
                Cancelar
              </button>
            </div>
          </>
        ) : null}
      </Modal>
    </>
  );
};

export default ReplayVideoDownload;
